import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import createIcon from "@impero-com/common/img/create-icon.svg";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { ActionDropdownItem, ActionSpec, NoContent } from "@impero-com/ui";
import { TableRestore } from "@impero-com/ui/domain";
import { useNavigate, useParams } from "react-router-dom";

import activityIcon from "img/activity.svg";
import editIcon from "img/edit.svg";
import {
    buildTestControlCreateBulkUrl,
    buildTestControlCreateUrl,
    buildTestingControlListRestoreConfig,
    buildTestingProgramViewEditUrl,
    testingProgramIdParamSchema,
    buildTestingActivitiesForProgramUrl,
    testingProgramListUrl,
} from "org/controlTesting/urlUtils";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";

import { useTestingProgramDetailsQuery } from "./queries/useTestingProgramDetailsQuery";
import { useTestingProgramTestingControlsQuery } from "./queries/useTestingProgramTestingControlsQuery";
import { TestingControlList } from "./TestingControlList";

interface TestingProgramTestingControlListProps {
    testingProgramId: number;
}

export function TestingControlListForProgramPage() {
    const { id: unparsedTestingProgramId } = useParams();
    const testingProgramIdParseResult = testingProgramIdParamSchema.safeParse(
        unparsedTestingProgramId
    );
    if (testingProgramIdParseResult.success === false) {
        return <LoggedInHTTPError code={400} />;
    }
    const testingProgramId = testingProgramIdParseResult.data;

    return (
        <TableRestore
            config={buildTestingControlListRestoreConfig(testingProgramId)}
        >
            <TestingProgramTestingControlList
                testingProgramId={testingProgramId}
            />
        </TableRestore>
    );
}

function TestingProgramTestingControlList({
    testingProgramId,
}: TestingProgramTestingControlListProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: testingProgramDetails } =
        useTestingProgramDetailsQuery(testingProgramId);
    const { data } = useTestingProgramTestingControlsQuery(testingProgramId);
    const hasControls = data.content.length > 0;

    const createItems: ActionDropdownItem[] = [
        {
            key: "single",
            title: t("ControlTesting_Action_NewSingleTestControl"),
            description: t("ControlTesting_NewSingleTestControl_Description"),
            action: {
                onClick: () =>
                    navigate(buildTestControlCreateUrl(testingProgramId)),
            },
        },
        {
            key: "multiple",
            title: t("ControlTesting_Action_NewMultipleTestControl"),
            description: t("ControlTesting_NewMultipleTestControl_Description"),
            action: {
                onClick: () =>
                    navigate(buildTestControlCreateBulkUrl(testingProgramId)),
            },
        },
    ];

    const actions: ActionSpec[] = [
        {
            id: "testingActivitiesLink",
            icon: { svg: activityIcon },
            label: t("ControlTesting_Action_TestingActivities"),
            status: { type: "ENABLED" },
            link: {
                to: buildTestingActivitiesForProgramUrl(testingProgramId),
            },
        },
        {
            id: "editTestingProgram",
            icon: { svg: editIcon },
            label: t("ControlTesting_Action_EditProgram"),
            link: {
                to: buildTestingProgramViewEditUrl(testingProgramId),
            },
        },
        {
            id: "createTestControl",
            icon: { svg: createIcon },
            label: t("ControlTesting_Action_NewTestControl"),
            type: "Dropdown",
            items: createItems,
        },
    ];

    return (
        <OrgApplicationBar
            title={{
                title: testingProgramDetails.content.resource.name,
                breadCrumbs: [
                    {
                        label: t("ControlTesting_ModuleTitle"),
                        url: testingProgramListUrl,
                    },
                ],
            }}
            actions={{ id: "testingControlList", actions }}
        >
            {hasControls ? (
                <TestingControlList
                    data={data}
                    testingProgramDetails={testingProgramDetails}
                    testingProgramId={testingProgramId}
                />
            ) : (
                <NoContent
                    header={t("TestingControlList_NoContent_Header")}
                    description={t("TestingControlList_NoContent_Description")}
                    actionCreation={{
                        type: "ROUTER",
                        label: t("TestingControlList_NoContent_Action"),
                        to: buildTestControlCreateUrl(testingProgramId),
                    }}
                />
            )}
        </OrgApplicationBar>
    );
}
